/** Resolve a downloadable file URL from assorted report API response shapes. */
export const extractReportDownloadUrl = (payload) => {
  if (!payload) return '';
  if (typeof payload === 'string') {
    const s = payload.trim();
    return s.startsWith('http') || s.startsWith('/') ? s : '';
  }

  const roots = [payload, payload.data, payload.result, payload.report, payload.reportDetails].filter(Boolean);

  const pickString = (v) => {
    if (!v) return '';
    if (typeof v === 'string') {
      const s = v.trim();
      return s.startsWith('http') || s.startsWith('/') ? s : '';
    }
    if (typeof v === 'object') {
      return (
        pickString(v.fileUrl)
        || pickString(v.objectUrl)
        || pickString(v.url)
        || pickString(v.signedUrl)
        || pickString(v.s3Url)
        || pickString(v.downloadUrl)
        || pickString(v.pdfUrl)
        || pickString(v.reportUrl)
      );
    }
    return '';
  };

  for (const root of roots) {
    const direct = pickString(
      root.pdfUrl
        ?? root.fileUrl
        ?? root.url
        ?? root.reportUrl
        ?? root.downloadUrl
    );
    if (direct) return direct;

    const nested = pickString(
      root.reportDetails
        ?? root.pdfReportDetails
        ?? root.onePageReportDetails
        ?? root.flashReportDetails
        ?? root.report_url?.Report_s3Url
        ?? root.report_url
    );
    if (nested) return nested;

    const url = pickString(root);
    if (url) return url;
  }

  return '';
};

/** Map DPR/DSR log GET fields → per–report-type download URLs for the generate modal. */
export const extractReportUrlsFromLogRecord = (logRecord) => {
  if (!logRecord || typeof logRecord !== 'object') {
    return { pdf: '', flash: '', onepage: '' };
  }
  const rd = logRecord;
  const fromDetails = (key) => extractReportDownloadUrl(rd[key]);
  return {
    pdf: fromDetails('reportDetails') || fromDetails('pdfReportDetails') || fromDetails('dprPdfReportDetails'),
    flash: fromDetails('flashReportDetails') || fromDetails('shiftFlashReportDetails'),
    onepage: fromDetails('onePageReportDetails') || fromDetails('onePagePdfReportDetails')
  };
};

export const openReportDownloadUrl = (url) => {
  const u = String(url || '').trim();
  if (!u) return false;
  window.open(u, '_blank', 'noopener,noreferrer');
  return true;
};

export const triggerReportDownload = (fileUrl, filename = 'report.pdf') => {
  if (!fileUrl) return;
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadReportFromBlob = (blob, filename = 'report.pdf') => {
  const url = URL.createObjectURL(blob);
  triggerReportDownload(url, filename);
  URL.revokeObjectURL(url);
};

const getFilenameFromDisposition = (disposition) => {
  if (!disposition) return '';
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
  return match ? decodeURIComponent(match[1].replace(/"/g, '')) : '';
};

export const downloadReportFromResponse = async (response, defaultFilename = 'report.pdf') => {
  const contentType = response.headers.get('content-type') || '';
  const disposition = response.headers.get('content-disposition') || '';
  const filename = getFilenameFromDisposition(disposition) || defaultFilename;

  if (contentType.includes('application/pdf') || contentType.includes('octet-stream')) {
    const blob = await response.blob();
    downloadReportFromBlob(blob, filename);
    return true;
  }

  const data = await response.json().catch(() => null);
  const fileUrl = extractReportDownloadUrl(data);
  if (fileUrl) {
    triggerReportDownload(fileUrl, filename);
    return true;
  }
  return false;
};

export const getDprReportFilename = (reportType, formattedLogDate, shift) => {
  const datePart = formattedLogDate || 'report';
  const typeLabels = {
    flash: 'flash-report',
    pdf: 'welding-report',
    onepage: 'one-page-report',
  };
  const base = typeLabels[reportType] || 'dpr-report';
  const shiftPart = reportType === 'flash' && shift ? `-${shift}` : '';
  return `${base}-${datePart}${shiftPart}.pdf`;
};
